import React, { useState, useEffect } from 'react';
import {
  Play,
  BarChart3,
  Search,
  FileText,
  Zap,
  RotateCw,
  Circle,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { agentService } from '../API/agentService';

const AgentMonitor = ({ agents, onRefresh }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentLogs, setAgentLogs] = useState({});
  const [runningAgents, setRunningAgents] = useState(new Set());
  const [sessionStatus, setSessionStatus] = useState(null);

  useEffect(() => {
    loadSessionStatus();
  }, []);

  const loadSessionStatus = async () => {
    try {
      const status = await agentService.getSessionStatus();
      setSessionStatus(status);
    } catch (error) {
      console.error('Failed to load session status:', error);
    }
  };

  const handleRunAgent = async (agentId, options = {}) => {
    setRunningAgents(prev => new Set([...prev, agentId]));

    try {
      const result = await agentService.runAgent(agentId, options);

      // Update logs
      setAgentLogs(prev => ({
        ...prev,
        [agentId]: [
          ...(prev[agentId] || []),
          {
            timestamp: result.timestamp,
            success: result.success,
            output: result.output || result.error,
            options
          }
        ].slice(-10) // Keep last 10 logs
      }));

      if (result.success) {
        onRefresh();
      }
    } catch (error) {
      console.error(`Failed to run agent ${agentId}:`, error);
    } finally {
      setRunningAgents(prev => {
        const newSet = new Set(prev);
        newSet.delete(agentId);
        return newSet;
      });
    }
  };

  const getAgentStatusIcon = (agent) => {
    if (runningAgents.has(agent.id)) {
      return <RotateCw className="animate-spin text-blue-500" size={20} />;
    }

    switch (agent.status) {
      case 'active':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'disabled':
        return <Circle className="text-gray-400" size={20} />;
      case 'error':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <AlertCircle className="text-yellow-500" size={20} />;
    }
  };

  const getTimeSinceLastRun = (timestamp) => {
    const now = new Date();
    const lastRun = new Date(timestamp);
    const diffInMinutes = Math.floor((now - lastRun) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const renderAgentCard = (agent) => (
    <div key={agent.id} className="agent-card">
      <div className="agent-card-header">
        <div className="agent-info">
          <div className="agent-icon">{getAgentStatusIcon(agent)}</div>
          <div>
            <h4 className="agent-name">{agent.name}</h4>
            <p className="agent-description" id={`${agent.id}-description`}>{agent.description}</p>
          </div>
        </div>
        <div className="agent-status">
          <span className={`status-badge ${agent.status}`}>
            {agent.status}
          </span>
        </div>
      </div>

      <div className="agent-details">
        <div className="agent-detail-item">
          <span className="detail-label">Last Run:</span>
          <span className="detail-value">{getTimeSinceLastRun(agent.lastRun)}</span>
        </div>

        {agent.nextScheduled && (
          <div className="agent-detail-item">
            <span className="detail-label">Next Scheduled:</span>
            <span className="detail-value">{getTimeSinceLastRun(agent.nextScheduled)}</span>
          </div>
        )}

        <div className="agent-detail-item">
          <span className="detail-label">Triggers:</span>
          <span className="detail-value">{agent.triggers.join(', ')}</span>
        </div>
      </div>

      <div className="agent-actions">
        <button
          className="btn btn-primary"
          onClick={() => handleRunAgent(agent.id)}
          disabled={runningAgents.has(agent.id) || !agent.enabled}
          aria-label={`Run ${agent.name} agent`}
          aria-describedby={`${agent.id}-description`}
        >
          {runningAgents.has(agent.id) ? (
            <>
              <RotateCw className="animate-spin" size={16} />
              <span>Running...</span>
            </>
          ) : (
            <>
              <Play size={16} />
              <span>Run Now</span>
            </>
          )}
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => setSelectedAgent(agent)}
          aria-label={`View execution logs for ${agent.name}`}
        >
          <BarChart3 size={16} />
          <span>View Logs</span>
        </button>

        {agent.id === 'quality_guardian' && (
          <button
            className="btn btn-secondary"
            onClick={() => handleRunAgent(agent.id, { files: 'staged' })}
            disabled={runningAgents.has(agent.id)}
            aria-label="Review staged files with Quality Guardian"
          >
            <Search size={16} />
            <span>Review Staged</span>
          </button>
        )}

        {agent.id === 'implementation_coordinator' && (
          <button
            className="btn btn-success"
            onClick={() => handleRunAgent(agent.id, { type: 'standup' })}
            disabled={runningAgents.has(agent.id)}
            aria-label="Run project coordination standup"
          >
            <FileText size={16} />
            <span>Project Status</span>
          </button>
        )}

        {agent.id === 'performance_monitor' && (
          <button
            className="btn btn-secondary"
            onClick={() => handleRunAgent(agent.id, { benchmark: true })}
            disabled={runningAgents.has(agent.id)}
            aria-label="Run performance benchmark"
          >
            <Zap size={16} />
            <span>Performance Check</span>
          </button>
        )}
      </div>

      {agentLogs[agent.id] && (
        <div className="agent-recent-logs">
          <h5>Recent Activity</h5>
          {agentLogs[agent.id].slice(-3).map((log, index) => (
            <div key={index} className={`log-entry ${log.success ? 'success' : 'error'}`}>
              <span className="log-time">{getTimeSinceLastRun(log.timestamp)}</span>
              <span className="log-message">
                {log.success ? <CheckCircle className="inline text-green-500" size={14} /> : <XCircle className="inline text-red-500" size={14} />} {log.output?.substring(0, 100)}...
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAgentLogs = () => (
    <div className="agent-logs-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{selectedAgent.name} - Execution Logs</h3>
          <button
            className="btn btn-secondary"
            onClick={() => setSelectedAgent(null)}
          >
            <X size={16} />
            <span>Close</span>
          </button>
        </div>

        <div className="logs-container">
          {agentLogs[selectedAgent.id]?.length > 0 ? (
            <div className="logs-list">
              {agentLogs[selectedAgent.id].map((log, index) => (
                <div key={index} className={`log-entry detailed ${log.success ? 'success' : 'error'}`}>
                  <div className="log-header">
                    <span className="log-status">
                      {log.success ? (
                        <>
                          <CheckCircle className="inline text-green-500" size={14} />
                          <span>Success</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="inline text-red-500" size={14} />
                          <span>Error</span>
                        </>
                      )}
                    </span>
                    <span className="log-timestamp">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>

                  {log.options && Object.keys(log.options).length > 0 && (
                    <div className="log-options">
                      <strong>Options:</strong> {JSON.stringify(log.options)}
                    </div>
                  )}

                  <div className="log-output">
                    <pre>{log.output}</pre>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-logs">
              <p>No execution logs available for this agent.</p>
              <button
                className="btn btn-primary"
                onClick={() => handleRunAgent(selectedAgent.id)}
              >
                Run Agent to Generate Logs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="agent-monitor">
      {/* Session Status */}
      {sessionStatus && (
        <div className="dashboard-card session-status">
          <div className="card-header">
            <h3 className="card-title">Session Coordination</h3>
            <button className="btn btn-secondary" onClick={loadSessionStatus}>
              🔄 Refresh
            </button>
          </div>

          <div className="session-details">
            <div className="session-detail">
              <span className="detail-label">Session ID:</span>
              <span className="detail-value">{sessionStatus.sessionId || 'Not registered'}</span>
            </div>

            <div className="session-detail">
              <span className="detail-label">Active Sessions:</span>
              <span className="detail-value">{sessionStatus.activeSessions}</span>
            </div>

            {sessionStatus.lockedFiles?.length > 0 && (
              <div className="session-detail">
                <span className="detail-label">Locked Files:</span>
                <div className="locked-files">
                  {sessionStatus.lockedFiles.map((file, index) => (
                    <span key={index} className="file-tag">{file}</span>
                  ))}
                </div>
              </div>
            )}

            {sessionStatus.conflicts?.length > 0 && (
              <div className="session-detail warning">
                <span className="detail-label">⚠️ Conflicts:</span>
                <div className="conflicts">
                  {sessionStatus.conflicts.map((conflict, index) => (
                    <span key={index} className="conflict-tag">{conflict}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agents Grid */}
      <div className="agents-grid">
        {agents.map(renderAgentCard)}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card quick-actions">
        <div className="card-header">
          <h3 className="card-title">Quick Actions</h3>
        </div>

        <div className="actions-grid">
          <button
            className="action-button"
            onClick={() => handleRunAgent('implementation_coordinator')}
            disabled={runningAgents.has('implementation_coordinator')}
          >
            📋 Project Status Check
          </button>

          <button
            className="action-button"
            onClick={async () => {
              try {
                await agentService.runStandup();
                onRefresh();
              } catch (error) {
                console.error('Failed to run standup:', error);
              }
            }}
          >
            📊 Daily Standup
          </button>

          <button
            className="action-button"
            onClick={() => handleRunAgent('quality_guardian', { comprehensive: true })}
            disabled={runningAgents.has('quality_guardian')}
          >
            🔍 Full Quality Review
          </button>

          <button
            className="action-button"
            onClick={() => handleRunAgent('performance_monitor', { benchmark: true })}
            disabled={runningAgents.has('performance_monitor')}
          >
            ⚡ Performance Check
          </button>
        </div>
      </div>

      {/* Agent Logs Modal */}
      {selectedAgent && renderAgentLogs()}
    </div>
  );
};

export default AgentMonitor;